import React, { useState, useEffect } from 'react';
import { cityAPI } from '../services/api';
import { City } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  SelectChangeEvent
} from '@mui/material';
import { Edit, Delete, Refresh, Add } from '@mui/icons-material';
import TranslationInput from '../components/TranslationInput';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

const Cities: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCityId, setEditingCityId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    country: '',
    networkId: '',
    authority: '',
    latitude: '',
    longitude: '',
    timezone: ''
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    fetchCities();
  }, []);

  const fetchCities = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await cityAPI.getAll();
      setCities(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load cities');
    } finally {
      setLoading(false);
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormError('');
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    setForm({ ...form, [name]: e.target.value });
    setFormError('');
  };

  const openAddModal = () => {
    setIsEditMode(false);
    setEditingCityId(null);
    setForm({ name: '', country: '', networkId: '', authority: '', latitude: '', longitude: '', timezone: '' });
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (city: City) => {
    setIsEditMode(true);
    setEditingCityId(city.id!);
    setForm({
      name: city.name,
      country: city.country,
      networkId: city.networkId,
      authority: city.authority,
      latitude: city.latitude?.toString() || '',
      longitude: city.longitude?.toString() || '',
      timezone: city.timezone || ''
    });
    setFormError('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingCityId(null);
    setForm({ name: '', country: '', networkId: '', authority: '', latitude: '', longitude: '', timezone: '' });
    setFormError('');
  };

  const handleSaveCity = async () => {
    if (!form.name.trim() || !form.country.trim() || !form.networkId.trim() || !form.authority.trim()) {
      setFormError('All fields are required.');
      return;
    }
    
    // Validate coordinates if provided
    if (form.latitude.trim() && form.longitude.trim()) {
      const lat = parseFloat(form.latitude);
      const lng = parseFloat(form.longitude);
      if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        setFormError('Invalid coordinates. Latitude must be between -90 and 90, longitude between -180 and 180.');
        return;
      }
    }

    setSaving(true);
    try {
      if (isEditMode && editingCityId) {
        await cityAPI.update(editingCityId, {
          name: form.name.trim(),
          country: form.country.trim(),
          networkId: form.networkId.trim(),
          authority: form.authority.trim(),
          latitude: form.latitude.trim() ? parseFloat(form.latitude) : undefined,
          longitude: form.longitude.trim() ? parseFloat(form.longitude) : undefined,
          timezone: form.timezone.trim() || undefined
        });
        setSuccessMsg('City updated successfully!');
      } else {
        await cityAPI.create({
          name: form.name.trim(),
          country: form.country.trim(),
          networkId: form.networkId.trim(),
          authority: form.authority.trim(),
          latitude: form.latitude.trim() ? parseFloat(form.latitude) : undefined,
          longitude: form.longitude.trim() ? parseFloat(form.longitude) : undefined,
          timezone: form.timezone.trim() || undefined
        });
        setSuccessMsg('City created successfully!');
      }
      closeModal();
      fetchCities();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'create'} city`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this city?')) return;
    
    setDeletingId(id);
    try {
      await cityAPI.delete(id);
      setSuccessMsg('City deleted successfully!');
      fetchCities();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete city');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Cities Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchCities}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add City
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Network ID</TableCell>
                  <TableCell>Authority</TableCell>
                  <TableCell>Coordinates</TableCell>
                  <TableCell>Timezone</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No cities found.</TableCell>
                  </TableRow>
                ) : cities.map(city => (
                  <TableRow key={city.id} hover>
                    <TableCell>{city.name}</TableCell>
                    <TableCell>{city.country}</TableCell>
                    <TableCell>
                      <Chip label={city.networkId} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{city.authority}</TableCell>
                    <TableCell>
                      {city.latitude && city.longitude ? (
                        <Tooltip title={`${city.latitude}, ${city.longitude}`}>
                          <Chip 
                            label={`${city.latitude.toFixed(4)}, ${city.longitude.toFixed(4)}`} 
                            size="small" 
                            color="secondary" 
                            variant="outlined"
                          />
                        </Tooltip>
                      ) : (
                        <Chip label="Not set" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      {city.timezone ? (
                        <Chip label={city.timezone} size="small" color="info" variant="outlined" />
                      ) : (
                        <Chip label="Not set" size="small" color="default" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>{city.lastUpdated ? new Date(city.lastUpdated).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit City">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(city)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete City">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(city.id!)}
                            disabled={deletingId === city.id}
                          >
                            {deletingId === city.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      <Dialog open={showModal} onClose={closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit City' : 'Add New City'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TranslationInput
                label="Name"
                value={form.name}
                onChange={(value) => setForm({ ...form, name: value })}
                entityType="CITY"
                entityId={isEditMode && editingCityId ? editingCityId : 'new'}
                fieldName="name"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Country</InputLabel>
                <Select
                  name="country"
                  value={form.country}
                  onChange={handleSelectInput}
                  label="Country"
                >
                  <MenuItem value="India">India</MenuItem>
                  <MenuItem value="Dubai">Dubai</MenuItem>
                  <MenuItem value="Thailand">Thailand</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Network ID" name="networkId" value={form.networkId} onChange={handleInput} fullWidth required />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Authority" name="authority" value={form.authority} onChange={handleInput} fullWidth required />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                label="Latitude" 
                name="latitude" 
                value={form.latitude} 
                onChange={handleInput} 
                fullWidth 
                placeholder="e.g., 28.7041"
                helperText="Optional: City center latitude (-90 to 90)"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                label="Longitude" 
                name="longitude" 
                value={form.longitude} 
                onChange={handleInput} 
                fullWidth 
                placeholder="e.g., 77.1025"
                helperText="Optional: City center longitude (-180 to 180)"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Timezone</InputLabel>
                <Select
                  name="timezone"
                  value={form.timezone}
                  onChange={handleSelectInput}
                  label="Timezone"
                >
                  <MenuItem value="">Select timezone (optional)</MenuItem>
                  <MenuItem value="Asia/Kolkata">Asia/Kolkata (IST - UTC+5:30)</MenuItem>
                  <MenuItem value="Asia/Dubai">Asia/Dubai (GST - UTC+4)</MenuItem>
                  <MenuItem value="Asia/Singapore">Asia/Singapore (SGT - UTC+8)</MenuItem>
                  <MenuItem value="Asia/Tokyo">Asia/Tokyo (JST - UTC+9)</MenuItem>
                  <MenuItem value="Asia/Shanghai">Asia/Shanghai (CST - UTC+8)</MenuItem>
                  <MenuItem value="Asia/Seoul">Asia/Seoul (KST - UTC+9)</MenuItem>
                  <MenuItem value="Asia/Bangkok">Asia/Bangkok (ICT - UTC+7)</MenuItem>
                  <MenuItem value="Asia/Manila">Asia/Manila (PHT - UTC+8)</MenuItem>
                  <MenuItem value="Asia/Jakarta">Asia/Jakarta (WIB - UTC+7)</MenuItem>
                  <MenuItem value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur (MYT - UTC+8)</MenuItem>
                  <MenuItem value="Europe/London">Europe/London (GMT/BST - UTC+0/+1)</MenuItem>
                  <MenuItem value="Europe/Paris">Europe/Paris (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Berlin">Europe/Berlin (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Rome">Europe/Rome (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Madrid">Europe/Madrid (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Amsterdam">Europe/Amsterdam (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Brussels">Europe/Brussels (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Vienna">Europe/Vienna (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Zurich">Europe/Zurich (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Stockholm">Europe/Stockholm (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Oslo">Europe/Oslo (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Copenhagen">Europe/Copenhagen (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Helsinki">Europe/Helsinki (EET/EEST - UTC+2/+3)</MenuItem>
                  <MenuItem value="Europe/Athens">Europe/Athens (EET/EEST - UTC+2/+3)</MenuItem>
                  <MenuItem value="Europe/Prague">Europe/Prague (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Warsaw">Europe/Warsaw (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Budapest">Europe/Budapest (CET/CEST - UTC+1/+2)</MenuItem>
                  <MenuItem value="Europe/Bucharest">Europe/Bucharest (EET/EEST - UTC+2/+3)</MenuItem>
                  <MenuItem value="Europe/Sofia">Europe/Sofia (EET/EEST - UTC+2/+3)</MenuItem>
                  <MenuItem value="Europe/Kiev">Europe/Kiev (EET/EEST - UTC+2/+3)</MenuItem>
                  <MenuItem value="Europe/Moscow">Europe/Moscow (MSK - UTC+3)</MenuItem>
                  <MenuItem value="Europe/Istanbul">Europe/Istanbul (TRT - UTC+3)</MenuItem>
                  <MenuItem value="America/New_York">America/New_York (EST/EDT - UTC-5/-4)</MenuItem>
                  <MenuItem value="America/Chicago">America/Chicago (CST/CDT - UTC-6/-5)</MenuItem>
                  <MenuItem value="America/Denver">America/Denver (MST/MDT - UTC-7/-6)</MenuItem>
                  <MenuItem value="America/Los_Angeles">America/Los_Angeles (PST/PDT - UTC-8/-7)</MenuItem>
                  <MenuItem value="America/Toronto">America/Toronto (EST/EDT - UTC-5/-4)</MenuItem>
                  <MenuItem value="America/Vancouver">America/Vancouver (PST/PDT - UTC-8/-7)</MenuItem>
                  <MenuItem value="America/Mexico_City">America/Mexico_City (CST/CDT - UTC-6/-5)</MenuItem>
                  <MenuItem value="America/Sao_Paulo">America/Sao_Paulo (BRT/BRST - UTC-3/-2)</MenuItem>
                  <MenuItem value="America/Buenos_Aires">America/Buenos_Aires (ART - UTC-3)</MenuItem>
                  <MenuItem value="America/Santiago">America/Santiago (CLT/CLST - UTC-3/-2)</MenuItem>
                  <MenuItem value="America/Lima">America/Lima (PET - UTC-5)</MenuItem>
                  <MenuItem value="America/Bogota">America/Bogota (COT - UTC-5)</MenuItem>
                  <MenuItem value="America/Caracas">America/Caracas (VET - UTC-4)</MenuItem>
                  <MenuItem value="Africa/Cairo">Africa/Cairo (EET - UTC+2)</MenuItem>
                  <MenuItem value="Africa/Johannesburg">Africa/Johannesburg (SAST - UTC+2)</MenuItem>
                  <MenuItem value="Africa/Lagos">Africa/Lagos (WAT - UTC+1)</MenuItem>
                  <MenuItem value="Africa/Nairobi">Africa/Nairobi (EAT - UTC+3)</MenuItem>
                  <MenuItem value="Africa/Casablanca">Africa/Casablanca (WET/WEST - UTC+0/+1)</MenuItem>
                  <MenuItem value="Africa/Algiers">Africa/Algiers (CET - UTC+1)</MenuItem>
                  <MenuItem value="Africa/Tunis">Africa/Tunis (CET - UTC+1)</MenuItem>
                  <MenuItem value="Africa/Tripoli">Africa/Tripoli (EET - UTC+2)</MenuItem>
                  <MenuItem value="Africa/Khartoum">Africa/Khartoum (CAT - UTC+2)</MenuItem>
                  <MenuItem value="Africa/Addis_Ababa">Africa/Addis_Ababa (EAT - UTC+3)</MenuItem>
                  <MenuItem value="Africa/Dar_es_Salaam">Africa/Dar_es_Salaam (EAT - UTC+3)</MenuItem>
                  <MenuItem value="Africa/Kinshasa">Africa/Kinshasa (WAT - UTC+1)</MenuItem>
                  <MenuItem value="Africa/Luanda">Africa/Luanda (WAT - UTC+1)</MenuItem>
                  <MenuItem value="Australia/Sydney">Australia/Sydney (AEST/AEDT - UTC+10/+11)</MenuItem>
                  <MenuItem value="Australia/Melbourne">Australia/Melbourne (AEST/AEDT - UTC+10/+11)</MenuItem>
                  <MenuItem value="Australia/Brisbane">Australia/Brisbane (AEST - UTC+10)</MenuItem>
                  <MenuItem value="Australia/Perth">Australia/Perth (AWST - UTC+8)</MenuItem>
                  <MenuItem value="Australia/Adelaide">Australia/Adelaide (ACST/ACDT - UTC+9:30/+10:30)</MenuItem>
                  <MenuItem value="Australia/Darwin">Australia/Darwin (ACST - UTC+9:30)</MenuItem>
                  <MenuItem value="Pacific/Auckland">Pacific/Auckland (NZST/NZDT - UTC+12/+13)</MenuItem>
                  <MenuItem value="Pacific/Fiji">Pacific/Fiji (FJT - UTC+12)</MenuItem>
                  <MenuItem value="Pacific/Guam">Pacific/Guam (ChST - UTC+10)</MenuItem>
                  <MenuItem value="Pacific/Honolulu">Pacific/Honolulu (HST - UTC-10)</MenuItem>
                </Select>
                <FormHelperText>Optional: Select the city's timezone for accurate time calculations</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button onClick={handleSaveCity} variant="contained" color="primary" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (isEditMode ? 'Update' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cities; 